import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { createThreadStatePrefetchOptions } from "@/features/thread/hooks/threadQueryOptions";
import { apiGet, hasAimgToken, useAimgViewerContext } from "@/shared/api";

/**
 * PC版カタログセルのhover/focus時に、スレッドを開くのに必要な state クエリを
 * 先読みで温める。`useThread` の stateQuery と同一のキー生成関数
 * (`threadStateQueryKey`)・同一のリクエスト組み立て(`createThreadStateRequest`)・
 * 同一のfetcher(`apiGet`)を経由するため、viewer context世代を含むキーが
 * `useThread` 側と必ず一致する。クリック時に先読みが進行中なら同じrequestを
 * 再利用する。完了済みcacheは現在contextのstateとして再検証し、mount後の
 * 取得成功までは表示判定に使用しない。
 *
 * token未発行の匿名閲覧者に対しては呼び出さない: hoverのような受動的操作を
 * 契機にPOST /tokens(フィンガープリント送信)を新規発行してしまうと、従来
 * 「スレッドを実際に開くまで発生しなかった」プライバシー境界が変わってしまう
 * ため、`hasAimgToken()` で既発行を確認できた場合のみ先読みする。
 */
export function usePrefetchThreadState(): (threadId: string) => void {
  const queryClient = useQueryClient();
  const viewerContext = useAimgViewerContext();

  return useCallback(
    (threadId: string): void => {
      if (!threadId) return;
      if (!hasAimgToken()) return;
      void queryClient.prefetchQuery(
        createThreadStatePrefetchOptions(threadId, viewerContext, apiGet),
      );
    },
    [queryClient, viewerContext],
  );
}
